import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './PrivacyPolicy.module.css';

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1>Terms of Service</h1>
        <p className={styles.lastUpdated}>Last updated: December 7, 2024</p>

        <section className={styles.section}>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using WealthPlay ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.
          </p>
        </section>

        <section className={styles.section}>
          <h2>2. Description of Service</h2>
          <p>
            WealthPlay is a financial gamification application that helps users track spending, manage budgets, and learn financial concepts through interactive games and quests. The Service integrates with financial institutions through Plaid API to provide transaction tracking and budgeting features.
          </p>
        </section>

        <section className={styles.section}>
          <h2>3. User Registration and Accounts</h2>
          <p>To use certain features of the Service, you must register for an account. You agree to:</p>
          <ul>
            <li>Provide accurate, current, and complete information during registration</li>
            <li>Maintain and promptly update your account information</li>
            <li>Maintain the security of your password and accept all risks of unauthorized access</li>
            <li>Notify us immediately if you discover any unauthorized use of your account</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>4. Eligibility</h2>
          <p>
            You must be at least 13 years old to use the Service. By using the Service, you represent and warrant that you meet this age requirement and have the legal capacity to enter into these Terms.
          </p>
        </section>

        <section className={styles.section}>
          <h2>5. Financial Account Connection</h2>
          <p>
            By connecting your financial accounts through Plaid, you authorize us to access your financial information for the purpose of providing the Service. You acknowledge that:
          </p>
          <ul>
            <li>You have the right to provide access to the connected accounts</li>
            <li>The accuracy of financial data depends on your financial institution</li>
            <li>You can disconnect your accounts at any time through the app</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>6. User Conduct</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any unlawful purpose or in violation of any regulations</li>
            <li>Attempt to gain unauthorized access to any portion of the Service</li>
            <li>Interfere with or disrupt the Service or servers connected to the Service</li>
            <li>Impersonate any person or entity or falsely state your affiliation with any person or entity</li>
            <li>Upload or transmit viruses or any other type of malicious code</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>7. Intellectual Property</h2>
          <p>
            All content, features, and functionality of the Service, including but not limited to text, graphics, logos, and software, are the exclusive property of WealthPlay or its licensors and are protected by copyright, trademark, and other intellectual property laws.
          </p>
        </section>

        <section className={styles.section}>
          <h2>8. Privacy</h2>
          <p>
            Your use of the Service is also governed by our Privacy Policy. Please review our Privacy Policy, which also governs the Service and informs users of our data collection practices.
          </p>
        </section>

        <section className={styles.section}>
          <h2>9. Disclaimers</h2>
          <p>
            <strong>No Financial Advice:</strong> WealthPlay does not provide financial, investment, tax, legal, or accounting advice. The Service is for informational and educational purposes only. You should consult with qualified professionals for advice specific to your situation.
          </p>
          <p>
            <strong>Accuracy of Information:</strong> While we strive to provide accurate information, we make no warranties about the completeness, reliability, or accuracy of the information provided through the Service.
          </p>
        </section>

        <section className={styles.section}>
          <h2>10. Limitation of Liability</h2>
          <p>
            To the fullest extent permitted by law, WealthPlay and its affiliates, officers, employees, agents, partners, and licensors shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses resulting from your use of the Service.
          </p>
        </section>

        <section className={styles.section}>
          <h2>11. Indemnification</h2>
          <p>
            You agree to defend, indemnify, and hold harmless WealthPlay and its affiliates from and against any claims, liabilities, damages, losses, and expenses arising out of or in any way connected with your access to or use of the Service or your violation of these Terms.
          </p>
        </section>

        <section className={styles.section}>
          <h2>12. Termination</h2>
          <p>
            We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach these Terms. Upon termination, your right to use the Service will immediately cease.
          </p>
        </section>

        <section className={styles.section}>
          <h2>13. Modifications to Service and Terms</h2>
          <p>
            We reserve the right to modify or discontinue the Service at any time without notice. We may also revise these Terms at any time by updating this page. Your continued use of the Service after any changes constitutes your acceptance of the new Terms.
          </p>
        </section>

        <section className={styles.section}>
          <h2>14. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Any legal action or proceeding arising under these Terms will be brought exclusively in the courts located in the United States.
          </p>
        </section>

        <section className={styles.section}>
          <h2>15. Contact Information</h2>
          <p>If you have any questions about these Terms of Service, please contact us at:</p>
          <div className={styles.contactInfo}>
            <p>Email: devan@wealthplay.com</p>
            <p>Subject: Terms of Service Inquiry</p>
          </div>
        </section>

        <div style={{ 
          textAlign: 'center', 
          marginTop: '2rem',
          marginBottom: '2rem',
          paddingTop: '1rem',
          borderTop: '1px solid #e5e7eb'
        }}>
          <p style={{ marginBottom: '20px' }}>
            View our{' '}
            <a href="#/privacy-policy" style={{ color: 'hsl(260, 80%, 74%)', textDecoration: 'underline' }}>
              Privacy Policy
            </a>
            {' • '}
            Want to delete your account? Visit our{' '}
            <a href="#/account-deletion" style={{ color: 'hsl(260, 80%, 74%)', textDecoration: 'underline' }}>
              Account Deletion
            </a>{' '}
            page.
          </p>
          <button 
            onClick={handleGoBack}
            style={{
              background: 'hsl(260, 80%, 74%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#8b5cf6'}
            onMouseOut={(e) => e.currentTarget.style.background = 'hsl(260, 80%, 74%)'}
          >
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;